/**
 * Trend detail — route /blog/trend/:id.
 *
 * Everything about one radar trend on its own page (the user's ask — the inbox
 * card alone gave "null details"): the full framing, momentum, sources, AND the
 * assessed relationship to our Ectoin evidence (direct / adjacent / off-domain
 * with a rationale, the matched claim cards, and any certificate overlap). The
 * watch/write/dismiss actions live here too; "Write" promotes it to a Kür topic.
 * English-only (Atlas).
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  Loader2,
  ExternalLink,
  Eye,
  PenLine,
  X,
  TrendingUp,
  FileText,
  Sparkles,
} from "lucide-react";
import { api, ApiError } from "../api";
import { EmptyState, useToast } from "../ui";
import Md from "../components/Md";
import {
  StatusPill,
  Eyebrow,
  EvidenceCard,
  TrendMatchBadge,
  CertOverlapBadges,
  benefitAreaLabel,
  BlogBackLink,
  type EviClaimLike,
} from "./blog/shared";

interface TrendDetail {
  id: string;
  term: string;
  headline: string | null;
  framing_md: string | null;
  benefit_area: string | null;
  evi_groundable: boolean;
  status: string;
  momentum_note: string | null;
  source: string;
  source_urls: string[];
  promoted_topic_id: string | null;
  write_eligible: boolean;
  claim_match: string | null;
  match_rationale: string | null;
  cert_overlap: string[];
  matched_claims: EviClaimLike[];
  anchor_claims: EviClaimLike[];
}

export default function BlogTrendDetailPage() {
  const { id = "" } = useParams();
  const toast = useToast();
  const [trend, setTrend] = useState<TrendDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deepening, setDeepening] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const d = await api.get<TrendDetail>(`/api/blog-dashboard/trends/${id}`);
      setTrend(d);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setNotFound(true);
      else console.error("load trend failed", e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const deepen = async () => {
    if (!trend) return;
    setDeepening(true);
    try {
      await api.post(`/api/blog-dashboard/trends/${trend.id}/deepen`);
      toast.ok("Framing deepened from the sources.");
      await load();
    } catch {
      toast.err("Deepen failed.");
    } finally {
      setDeepening(false);
    }
  };

  const setStatus = async (status: "watch" | "write" | "dismissed") => {
    if (!trend) return;
    setBusy(true);
    try {
      await api.patch(`/api/blog-dashboard/trends/${trend.id}`, { status });
      toast.ok(status === "write" ? "Promoted to a topic." : "Status updated.");
      await load();
    } catch (e) {
      const msg =
        e instanceof ApiError && e.status === 409
          ? "Write needs a mapped rubric with evidence."
          : "Update failed.";
      toast.err(msg);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !trend) {
    return (
      <div className="mx-auto max-w-4xl p-6 flex items-center gap-2 text-text-3">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading trend …
      </div>
    );
  }
  if (notFound || !trend) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <BackLink />
        <div className="mt-6">
          <EmptyState icon={<FileText className="h-6 w-6" />} title="Trend not found" hint="Head back to the dashboard." />
        </div>
      </div>
    );
  }

  return (
    <div className="relative mx-auto max-w-4xl px-4 md:px-6 py-6 space-y-6">
      <BackLink />

      {/* Header */}
      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {trend.benefit_area && <Eyebrow>{benefitAreaLabel(trend.benefit_area)}</Eyebrow>}
          <StatusPill status={trend.status} />
          <TrendMatchBadge match={trend.claim_match} />
          <CertOverlapBadges certs={trend.cert_overlap} />
        </div>
        <h1 className="inline-flex items-center gap-2 font-display text-2xl font-semibold leading-tight tracking-tight text-text">
          <TrendingUp className="h-6 w-6 shrink-0 text-accent-500" /> {trend.term}
        </h1>
        {trend.headline && <p className="text-base text-text-2">{trend.headline}</p>}
      </header>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 border-y border-border py-3">
        <ActionBtn active={trend.status === "write"} onClick={() => setStatus("write")} disabled={busy || !trend.write_eligible} icon={<PenLine className="h-4 w-4" />} primary>
          Write
        </ActionBtn>
        <ActionBtn active={trend.status === "watch"} onClick={() => setStatus("watch")} disabled={busy} icon={<Eye className="h-4 w-4" />}>
          Watch
        </ActionBtn>
        <ActionBtn active={trend.status === "dismissed"} onClick={() => setStatus("dismissed")} disabled={busy} icon={<X className="h-4 w-4" />}>
          Dismiss
        </ActionBtn>
        {!trend.write_eligible && (
          <span className="text-xs text-text-3">Write needs a mapped rubric with evidence.</span>
        )}
        {trend.promoted_topic_id && (
          <Link
            to={`/blog/topic/${trend.promoted_topic_id}`}
            className="ml-auto inline-flex items-center gap-1.5 text-sm text-accent-700 hover:text-accent-600 dark:text-accent-300"
          >
            Open topic <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </div>

      {/* Relationship to our evidence */}
      {(trend.claim_match || trend.match_rationale) && (
        <section className="rounded-2xl border border-border bg-surface-1 p-4 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <SectionLabel>Relationship to our evidence</SectionLabel>
            <TrendMatchBadge match={trend.claim_match} />
            <CertOverlapBadges certs={trend.cert_overlap} />
          </div>
          {trend.match_rationale && <p className="text-sm text-text-2">{trend.match_rationale}</p>}
        </section>
      )}

      {/* Framing — the writer's angle. Readable prose (not mini) + a "Deepen"
          action that re-reads the sources into a fuller brief. */}
      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SectionLabel>How the industry frames it</SectionLabel>
          <button
            onClick={deepen}
            disabled={deepening || trend.source_urls.length === 0}
            title={trend.source_urls.length === 0 ? "No sources to deepen from." : "Re-read the sources into a fuller brief"}
            className="inline-flex items-center gap-1.5 rounded-control border border-border px-2.5 py-1 text-xs font-medium text-text-2 hover:bg-surface-3 disabled:opacity-40"
          >
            {deepening ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {deepening ? "Deepening …" : "Deepen"}
          </button>
        </div>
        {trend.framing_md ? (
          <div className="max-w-[68ch] text-[0.98rem] leading-7 text-text-2 [&_p]:my-2.5">
            <Md>{trend.framing_md}</Md>
          </div>
        ) : (
          <p className="text-sm text-text-3">No framing yet — press Deepen to build one from the sources.</p>
        )}
      </section>

      {trend.momentum_note && (
        <section className="space-y-1">
          <SectionLabel>Momentum</SectionLabel>
          <p className="text-sm text-text-2">{trend.momentum_note}</p>
        </section>
      )}

      {/* Matched claims */}
      {trend.matched_claims.length > 0 && (
        <section className="space-y-2">
          <SectionLabel hint="Our Ectoin claims that overlap this trend — click for the full study.">
            Overlapping claims ({trend.matched_claims.length})
          </SectionLabel>
          <ClaimGrid claims={trend.matched_claims} />
        </section>
      )}

      {/* Anchor claims (writer grounding) */}
      {trend.anchor_claims.length > 0 && (
        <section className="space-y-2">
          <SectionLabel hint="The evidence a Kür article would be grounded in.">
            Writer grounding ({trend.anchor_claims.length})
          </SectionLabel>
          <ClaimGrid claims={trend.anchor_claims} />
        </section>
      )}

      {/* Sources */}
      {trend.source_urls.length > 0 && (
        <section className="space-y-1.5">
          <SectionLabel>Sources</SectionLabel>
          <ul className="space-y-1">
            {trend.source_urls.map((u) => (
              <li key={u}>
                <a
                  href={u}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 break-all text-sm text-accent-700 hover:text-accent-600 dark:text-accent-300"
                >
                  <ExternalLink className="h-3.5 w-3.5 shrink-0" /> {u}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ClaimGrid({ claims }: { claims: EviClaimLike[] }) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {claims.map((c) => (
        <Link key={c.id} to={`/claims/${c.id}`} className="block rounded-2xl transition hover:opacity-90">
          <EvidenceCard claim={c} />
        </Link>
      ))}
    </div>
  );
}

function ActionBtn({
  children,
  onClick,
  disabled,
  active,
  icon,
  primary,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  icon: ReactNode;
  primary?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={
        active
          ? "inline-flex items-center gap-1.5 rounded-control bg-accent-500 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          : primary
            ? "inline-flex items-center gap-1.5 rounded-control border border-accent-500/50 px-3 py-1.5 text-sm font-medium text-accent-700 hover:bg-accent-500/10 disabled:cursor-not-allowed disabled:opacity-40 dark:text-accent-300"
            : "inline-flex items-center gap-1.5 rounded-control border border-border px-3 py-1.5 text-sm text-text-2 hover:bg-surface-3 disabled:opacity-40"
      }
    >
      {icon}
      {children}
    </button>
  );
}

function SectionLabel({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <div>
      <h2 className="font-display text-sm font-semibold uppercase tracking-[0.1em] text-text-2">{children}</h2>
      {hint && <p className="mt-0.5 text-xs text-text-3">{hint}</p>}
    </div>
  );
}

function BackLink() {
  return <BlogBackLink to="/blog/dashboard?tab=trends" label="Back to Trend inbox" />;
}
